import * as React from 'react';

import type { PageProps } from 'gatsby';
import styled from 'styled-components';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import { Underline } from 'stile-shared/src/components/2024/common/Underline';

import { PageLayout } from 'templates/2024/PageLayout';

export { Head } from 'templates/2024/Head';

const MississippiAlignmentLayout = styled<typeof PageLayout>(PageLayout)`
  .main-content {
    margin-top: -12px;
  }
`;

const MississippiAlignmentTopSection = styled.div`
  background-color: transparent;

  .content {
    display: flex;
    flex-direction: column;
    position: relative;
    padding-bottom: 100px;

    @media (max-width: 768px) {
      padding-bottom: 48px;
    }

    h1 {
      margin-top: 80px;
      margin-bottom: 60px;
      width: 60%;

      @media (max-width: 768px) {
        margin-top: 38px;
        margin-bottom: 25px;
        width: 100%;
      }
    }

    > div {
      width: 38%;

      @media (max-width: 768px) {
        width: 100%;
      }
    }

    .underline {
      margin-top: 20px;
      margin-bottom: 30px;
    }
  }
`;

const SwipeAdviceSection = styled.div`
  .content {
    display: none;
    flex-direction: row;
    gap: 10px;
    padding-bottom: 12px;
    color: #706d69;
    p {
      margin: 0;
      padding: 0;
    }

    @media (max-width: 768px) {
      display: flex;
    }
  }
`;

const AlignmentSection = styled.div`
  background-color: transparent;

  .content {
    display: flex;
    flex-direction: column;
    position: relative;
    padding-bottom: 100px;

    .table {
      border-radius: 4px;
      border: var(--stile-grey-8) 1px solid;
      display: grid;
      grid-template-columns: 200px 100px 200px 300px 1fr;
      overflow-x: scroll;
      .header {
        display: contents;
        border-radius: 4px;
        > div {
          background: var(--stile-grey-2);
          padding: 10px;
          font-weight: 600;
          border-right: var(--stile-grey-3) 1px solid;
          border-bottom: var(--stile-grey-3) 1px solid;
          &:first-child {
            border-top-left-radius: 4px;
            background: var(--stile-grey-4);
          }
          &:last-child {
            border-top-right-radius: 4px;
          }
        }
      }
      .row {
        display: contents;

        > div {
          padding: 10px;
          border-right: var(--stile-grey-3) 1px solid;
          border-bottom: var(--stile-grey-3) 1px solid;

          &:first-child {
            background-color: #f0fef1;
          }
        }

        // every even child
        &:nth-child(odd) {
          > div {
            background-color: #fbfaf9;

            &:first-child {
              background-color: #d9f7da;
            }
          }
        }

        &.first-in-section {
          > div {
            border-top: var(--stile-grey-8) 1px solid;
            border-collapse: collapse;
          }
        }

        &:last-child {
          > div {
            border-bottom: none;
          }
        }
      }
    }
  }
`;

function TwentyTwentyFourMississippiAlignmentPage(props: PageProps) {
  return (
    <MississippiAlignmentLayout {...props}>
      <MississippiAlignmentTopSection>
        <div className="content">
          <h1>Mississippi College- and Career-Readiness Science Standards Correlation</h1>
          <div>
            Stile is aligned to the MS CCRS for 6th, 7th, and 8th grades, so you can end the
            guesswork and focus on what matters most: your students.
          </div>
          <Underline underlineNumber={1} speed="0.5s" />
          <Button
            style="primary"
            href="/states/mississippi/"
            label="Learn about Stile in Mississippi"
          />
        </div>
      </MississippiAlignmentTopSection>
      <SwipeAdviceSection>
        <div className="content">
          <p>Swipe across to view table</p>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.24648 5.35416H5.94857C6.1201 5.35416 6.26194 5.41023 6.37409 5.52239C6.48624 5.63454 6.54232 5.77638 6.54232 5.94791C6.54232 6.11943 6.48624 6.26127 6.37409 6.37343C6.26194 6.48558 6.1201 6.54166 5.94857 6.54166H3.37565C3.15135 6.54166 2.96332 6.46579 2.81159 6.31405C2.65985 6.16232 2.58398 5.9743 2.58398 5.74999V3.17707C2.58398 3.00555 2.64006 2.86371 2.75221 2.75155C2.86437 2.6394 3.00621 2.58332 3.17773 2.58332C3.34926 2.58332 3.4911 2.6394 3.60326 2.75155C3.71541 2.86371 3.77148 3.00555 3.77148 3.17707V4.18645C4.72148 3.40798 5.77044 2.81423 6.91836 2.4052C8.06628 1.99617 9.26037 1.79166 10.5007 1.79166C12.2687 1.79166 13.852 2.1677 15.2507 2.91978C16.6493 3.67186 17.6257 4.66145 18.1798 5.88853C18.2458 6.04686 18.2392 6.1953 18.16 6.33384C18.0809 6.47239 17.9621 6.54166 17.8038 6.54166C17.6059 6.54166 17.4244 6.48888 17.2595 6.38332C17.0946 6.27777 16.9593 6.13923 16.8538 5.9677C16.26 5.03089 15.409 4.2986 14.3007 3.77082C13.1923 3.24305 11.9257 2.97916 10.5007 2.97916C9.33954 2.97916 8.22461 3.18367 7.15586 3.5927C6.08711 4.00173 5.11732 4.58888 4.24648 5.35416ZM10.3621 18.4167C10.0454 18.4167 9.74197 18.3573 9.45169 18.2385C9.16142 18.1198 8.90412 17.9483 8.67982 17.7239L5.19648 14.2208C5.05135 14.0757 4.97548 13.8943 4.96888 13.6766C4.96228 13.4588 5.03155 13.2708 5.17669 13.1125L5.23607 13.0531C5.44718 12.842 5.69458 12.7002 5.97826 12.6276C6.26194 12.555 6.54232 12.5583 6.8194 12.6375L8.12565 13.0135V6.54166C8.12565 6.31735 8.20152 6.12933 8.35326 5.97759C8.50499 5.82586 8.69301 5.74999 8.91732 5.74999C9.14162 5.74999 9.32965 5.82586 9.48138 5.97759C9.63312 6.12933 9.70899 6.31735 9.70899 6.54166V14.0625C9.70899 14.3264 9.60343 14.5375 9.39232 14.6958C9.18121 14.8542 8.9503 14.9003 8.69961 14.8344L7.78919 14.5771L9.80794 16.5958C9.87392 16.6618 9.95638 16.7179 10.0553 16.7641C10.1543 16.8102 10.2566 16.8333 10.3621 16.8333H13.6673C14.1027 16.8333 14.4755 16.6783 14.7855 16.3682C15.0956 16.0581 15.2507 15.6854 15.2507 15.25V12.0833C15.2507 11.859 15.3265 11.671 15.4783 11.5193C15.63 11.3675 15.818 11.2917 16.0423 11.2917C16.2666 11.2917 16.4546 11.3675 16.6064 11.5193C16.7581 11.671 16.834 11.859 16.834 12.0833V15.25C16.834 16.1208 16.5239 16.8663 15.9038 17.4864C15.2836 18.1066 14.5382 18.4167 13.6673 18.4167H10.3621ZM11.2923 8.91666C11.5166 8.91666 11.7046 8.99252 11.8564 9.14426C12.0081 9.296 12.084 9.48402 12.084 9.70832V12.0833C12.084 12.3076 12.0081 12.4956 11.8564 12.6474C11.7046 12.7991 11.5166 12.875 11.2923 12.875C11.068 12.875 10.88 12.7991 10.7283 12.6474C10.5765 12.4956 10.5007 12.3076 10.5007 12.0833V9.70832C10.5007 9.48402 10.5765 9.296 10.7283 9.14426C10.88 8.99252 11.068 8.91666 11.2923 8.91666ZM13.6673 9.70832C13.8916 9.70832 14.0796 9.78419 14.2314 9.93593C14.3831 10.0877 14.459 10.2757 14.459 10.5V12.0833C14.459 12.3076 14.3831 12.4956 14.2314 12.6474C14.0796 12.7991 13.8916 12.875 13.6673 12.875C13.443 12.875 13.255 12.7991 13.1033 12.6474C12.9515 12.4956 12.8757 12.3076 12.8757 12.0833V10.5C12.8757 10.2757 12.9515 10.0877 13.1033 9.93593C13.255 9.78419 13.443 9.70832 13.6673 9.70832Z"
              fill="#928D88"
            />
          </svg>
        </div>
      </SwipeAdviceSection>
      <AlignmentSection>
        <div className="content">
          <div className="table">
            <div className="header">
              <div>Stile unit</div>
              <div>Grade</div>
              <div>Strand</div>
              <div>Standard</div>
              <div>Performance objective</div>
            </div>
            <div className="row">
              <div>Cells</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.1 Hierarchical Organization</div>
              <div>
                L.6.1.1 Use arguments supported by evidence in order to distinguish between living
                and non‐living things, including viruses and bacteria.
              </div>
            </div>
            <div className="row">
              <div>Cells</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.1 Hierarchical Organization</div>
              <div>L.6.1.2 Obtain and communicate evidence to support the cell theory.</div>
            </div>
            <div className="row">
              <div>Cells</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.1 Hierarchical Organization</div>
              <div>
                L.6.1.3 Develop and use models to explain how specific cellular components (cell
                wall, cell membrane, nucleus, chloroplast, vacuole, and mitochondria) function
                together to support the life of prokaryotic and eukaryotic organisms to include
                plants, animals, fungi, protists, and bacteria (not to include biochemical function
                of cells or cell part).
              </div>
            </div>
            <div className="row">
              <div>Cells</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.1 Hierarchical Organization</div>
              <div>
                L.6.1.4 Compare and contrast different cells in order to classify them as a protist,
                fungus, plant, or animal.
              </div>
            </div>
            <div className="row">
              <div>Cells</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.1 Hierarchical Organization</div>
              <div>L.6.1.5 Provide evidence that organisms are unicellular or multicellular.</div>
            </div>
            <div className="row">
              <div>Cells</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.1 Hierarchical Organization</div>
              <div>
                L.6.1.6 Develop and use models to show relationships among the increasing complexity
                of multicellular organisms (cells, tissues, organs, organ systems, organisms) and
                how they serve the needs of the organism.
              </div>
            </div>
            <div className="row">
              <div>Food Chains and Food Webs</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.3 Ecology and Interdependence</div>
              <div>
                L.6.3.1 Use scientific reasoning to explain differences between biotic and abiotic
                factors that demonstrate what living organisms need to survive.
              </div>
            </div>
            <div className="row">
              <div>Food Chains and Food Webs</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.3 Ecology and Interdependence</div>
              <div>
                L.6.3.2 Develop and use models to describe the levels of organization within
                ecosystems (species, populations, communities, ecosystems, and biomes).
              </div>
            </div>
            <div className="row">
              <div>Food Chains and Food Webs</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.3 Ecology and Interdependence</div>
              <div>
                L.6.3.3 Analyze cause and effect relationships to explore how changes in the
                physical environment (limiting factors, natural disasters) can lead to population
                changes within an ecosystem.
              </div>
            </div>
            <div className="row">
              <div>Food Chains and Food Webs</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.3 Ecology and Interdependence</div>
              <div>
                L.6.3.4 Investigate organism interactions in a competitive or mutually beneficial
                relationship (predation, competition, cooperation, or symbiotic relationships).
              </div>
            </div>
            <div className="row">
              <div>Food Chains and Food Webs</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.3 Ecology and Interdependence</div>
              <div>
                L.6.3.5 Develop and use food chains, webs, and pyramids to analyze how energy is
                transferred through an ecosystem from producers (autotrophs) to consumers
                (heterotrophs, including humans) to decomposers.
              </div>
            </div>
            <div className="row">
              <div>Classification</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.4 Adaptation and Diversity</div>
              <div>
                L.6.4.1 Compare and contrast modern classification techniques (e.g., analyzing
                genetic material) to the historical practices used by scientists such as Aristotle
                and Carolus Linnaeus.
              </div>
            </div>
            <div className="row">
              <div>Classification</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.4 Adaptation and Diversity</div>
              <div>
                L.6.4.2 Use classification methods to explore the diversity of organisms in kingdoms
                (animals, plants, fungi, protists, bacteria). Support claims that organisms have
                shared structural and behavioral characteristics.
              </div>
            </div>
            <div className="row">
              <div>Cells</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.4 Adaptation and Diversity</div>
              <div>
                L.6.4.3 Analyze and interpret data from observations to describe how fungi obtain
                energy and respond to stimuli (e.g., bread mold, rotting plant material).
              </div>
            </div>
            <div className="row">
              <div>Cells</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.4 Adaptation and Diversity</div>
              <div>
                L.6.4.4 Conduct investigations using a microscope or multimedia source to compare
                the characteristics of protists (euglena, paramecium, amoeba) and the methods they
                use to obtain energy and move through their environment (e.g., pond water).
              </div>
            </div>
            <div className="row">
              <div>Cells</div>
              <div>6</div>
              <div>Life Science</div>
              <div>L.6.4 Adaptation and Diversity</div>
              <div>
                L.6.4.5 Engage in scientific arguments to support claims that bacteria
                (Archaebacteria and Eubacteria) and viruses can be both helpful and harmful to other
                organisms and the environment.
              </div>
            </div>
            <div className="row">
              <div>Forces</div>
              <div>6</div>
              <div>Physical Science</div>
              <div>P.6.6 Motions, Forces, and Energy</div>
              <div>
                P.6.6.1 Use an engineering design process to create or improve safety devices (e.g.,
                seat belts, car seats, helmets) by applying Newton's Laws of motion. Use an
                engineering design process to define the problem, design, construct, evaluate, and
                improve the safety device.*
              </div>
            </div>
            <div className="row">
              <div>Forces</div>
              <div>6</div>
              <div>Physical Science</div>
              <div>P.6.6 Motions, Forces, and Energy</div>
              <div>
                P.6.6.2 Use mathematical computation and diagrams to calculate the sum of forces
                acting on various objects.
              </div>
            </div>
            <div className="row">
              <div>Forces</div>
              <div>6</div>
              <div>Physical Science</div>
              <div>P.6.6 Motions, Forces, and Energy</div>
              <div>
                P.6.6.3 Investigate and communicate ways to manipulate applied/frictional forces to
                improve movement of objects on various surfaces (e.g., athletic shoes, wheels on
                cars).
              </div>
            </div>
            <div className="row">
              <div>Forces</div>
              <div>6</div>
              <div>Physical Science</div>
              <div>P.6.6 Motions, Forces, and Energy</div>
              <div>
                P.6.6.4 Compare and contrast magnetic, electric, frictional, and gravitational
                forces.
              </div>
            </div>
            <div className="row">
              <div>Motion</div>
              <div>6</div>
              <div>Physical Science</div>
              <div>P.6.6 Motions, Forces, and Energy</div>
              <div>
                P.6.6.5 Conduct investigations to predict and explain the motion of an object
                according to its position, direction, speed, and acceleration.
              </div>
            </div>
            <div className="row">
              <div>Forces</div>
              <div>6</div>
              <div>Physical Science</div>
              <div>P.6.6 Motions, Forces, and Energy</div>
              <div>
                P.6.6.6 Investigate forces (gravity, friction, drag, lift, thrust) acting on objects
                (e.g., airplane, bicycle helmets). Use data to explain the differences between the
                forces in various environments.
              </div>
            </div>
            <div className="row">
              <div>Energy</div>
              <div>6</div>
              <div>Physical Science</div>
              <div>P.6.6 Motions, Forces, and Energy</div>
              <div>
                P.6.6.7 Determine the relationships between the concepts of potential, kinetic, and
                thermal energy.
              </div>
            </div>
            <div className="row">
              <div>Our Place in Space</div>
              <div>6</div>
              <div>Earth and Space Science</div>
              <div>E.6.8 Earth and the Universe</div>
              <div>
                E.6.8.1 Obtain, evaluate, and summarize past and present theories and evidence to
                explain the formation and composition of the universe.
              </div>
            </div>
            <div className="row">
              <div>Our Place in Space</div>
              <div>6</div>
              <div>Earth and Space Science</div>
              <div>E.6.8 Earth and the Universe</div>
              <div>
                E.6.8.2 Use graphical displays or models to explain the hierarchical structure
                (stars, galaxies, galactic clusters) of the universe.
              </div>
            </div>
            <div className="row">
              <div>Our Place in Space</div>
              <div>6</div>
              <div>Earth and Space Science</div>
              <div>E.6.8 Earth and the Universe</div>
              <div>
                E.6.8.3 Evaluate modern techniques used to explore our solar system's position in
                the universe.
              </div>
            </div>
            <div className="row">
              <div>Our Place in Space</div>
              <div>6</div>
              <div>Earth and Space Science</div>
              <div>E.6.8 Earth and the Universe</div>
              <div>
                E.6.8.4 Obtain and evaluate information to model and compare the characteristics and
                movements of objects in the solar system (including planets, moons, asteroids,
                comets, and meteors).
              </div>
            </div>
            <div className="row">
              <div>Our Place in Space</div>
              <div>6</div>
              <div>Earth and Space Science</div>
              <div>E.6.8 Earth and the Universe</div>
              <div>
                E.6.8.5 Construct explanations for how gravity affects the motion of objects in the
                solar system and tides on Earth.
              </div>
            </div>
            <div className="row">
              <div>Our Place in Space</div>
              <div>6</div>
              <div>Earth and Space Science</div>
              <div>E.6.8 Earth and the Universe</div>
              <div>
                E.6.8.6 Design models representing motions within the Sun‐Earth‐Moon system to
                explain phenomena observed from the Earth's surface (positions of celestial bodies,
                day and year, moon phases, solar and lunar eclipses, and tides).
              </div>
            </div>
            <div className="row">
              <div>Our Place in Space</div>
              <div>6</div>
              <div>Earth and Space Science</div>
              <div>E.6.8 Earth and the Universe</div>
              <div>
                E.6.8.7 Analyze and interpret data from the surface features of the Sun (e.g.,
                photosphere, corona, sunspots, prominences, and solar flares) to predict how these
                features may affect Earth.
              </div>
            </div>
            <div className="row">
              <div>Ecosystems</div>
              <div>7</div>
              <div>Life Science</div>
              <div>L.7.3 Ecology and Interdependence</div>
              <div>
                L.7.3.1 Analyze diagrams to provide evidence of the importance of the cycling of
                water, oxygen, carbon, and nitrogen through ecosystems to organisms.
              </div>
            </div>
            <div className="row">
              <div>Ecosystems</div>
              <div>7</div>
              <div>Life Science</div>
              <div>L.7.3 Ecology and Interdependence</div>
              <div>
                L.7.3.2 Analyze and interpret data to explain how the processes of photosynthesis,
                and cellular respiration (aerobic and anaerobic) work together to meet the needs of
                plants and animals.
              </div>
            </div>
            <div className="row">
              <div>Ecosystems</div>
              <div>7</div>
              <div>Life Science</div>
              <div>L.7.3 Ecology and Interdependence</div>
              <div>
                L.7.3.3 Use models to describe how food molecules (carbohydrates, lipids, proteins)
                are processed through chemical reactions using oxygen (aerobic) to form new
                molecules.
              </div>
            </div>
            <div className="row">
              <div>The Importance of Biodiversity</div>
              <div>7</div>
              <div>Life Science</div>
              <div>L.7.3 Ecology and Interdependence</div>
              <div>
                L.7.3.4 Explain how disruptions in cycles (e.g., water, oxygen, carbon, and
                nitrogen) affect biodiversity and ecosystem services (e.g., water, food, and
                medications) which are needed to sustain human life on Earth.
              </div>
            </div>
            <div className="row">
              <div>The Importance of Biodiversity</div>
              <div>7</div>
              <div>Life Science</div>
              <div>L.7.3 Ecology and Interdependence</div>
              <div>
                L.7.3.5 Design solutions for sustaining the health of ecosystems to maintain
                biodiversity and the resources needed by humans for survival (e.g., water
                purification, nutrient recycling, prevention of soil erosion, and prevention or
                management of invasive species).*
              </div>
            </div>
            <div className="row">
              <div>Elements and Compounds</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5A.1 Collect and evaluate qualitative data to describe substances using physical
                properties (state, boiling/melting point, density, heat/electrical conductivity,
                color, and magnetic properties).
              </div>
            </div>
            <div className="row">
              <div>Elements and Compounds</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5A.2 Analyze and interpret qualitative data to describe substances using
                chemical properties (the ability to burn or rust).
              </div>
            </div>
            <div className="row">
              <div>Elements and Compounds</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5A.3 Compare and contrast chemical and physical properties (e.g., combustion,
                oxidation, pH, solubility, reaction with water).
              </div>
            </div>
            <div className="row">
              <div>States of Matter</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5B.1 Make predictions about the effect of temperature and pressure on the
                relative motion of atoms and molecules (speed, expansion, and condensation) relative
                to recent breakthroughs in polymer and materials science (e.g. selfhealing
                protective films, silicone computer processors, pervious/porous concrete).
              </div>
            </div>
            <div className="row">
              <div>States of Matter</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5B.2 Use evidence from multiple scientific investigations to communicate the
                relationships between pressure, volume, density, and temperature of a gas.
              </div>
            </div>
            <div className="row">
              <div>States of Matter</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5B.3 Ask questions to explain how density of matter (observable in various
                objects) is affected by a change in heat and/or pressure.
              </div>
            </div>
            <div className="row">
              <div>Elements and Compounds</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>P.7.5C.1 Develop and use models that explain the structure of an atom.</div>
            </div>
            <div className="row">
              <div>Elements and Compounds</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5C.2 Use informational text to sequence the major discoveries leading to the
                current atomic model.
              </div>
            </div>
            <div className="row">
              <div>Elements and Compounds</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5C.3 Collect, organize, and interpret data from investigations to identify and
                analyze the relationships between the physical and chemical properties of elements,
                atoms, molecules, compounds, solutions, and mixtures.
              </div>
            </div>
            <div className="row">
              <div>Elements and Compounds</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5C.4 Predict the properties and interactions of elements using the periodic
                table (metals, nonmetals, reactivity, and conductors).
              </div>
            </div>
            <div className="row">
              <div>Elements and Compounds</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5C.5 Describe concepts used to construct chemical formulas (e.g. CH4, H20) to
                determine the number of atoms in a chemical formula.
              </div>
            </div>
            <div className="row">
              <div>Elements and Compounds</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5C.6 Using the periodic table, make predictions to explain how bonds (ionic and
                covalent) form between groups of elements (e.g., oxygen gas, ozone, water, table
                salt, and methane).
              </div>
            </div>
            <div className="row">
              <div>Elements and Compounds</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5D.1 Analyze evidence from scientific investigations to predict likely outcomes
                of chemical reactions
              </div>
            </div>
            <div className="row">
              <div>Physical and Chemical Change</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5D.2 Design and conduct scientific investigations to support evidence that
                chemical reactions (e.g., cooking, combustion, rusting, decomposition,
                photosynthesis, and cellular respiration) have occurred.
              </div>
            </div>
            <div className="row">
              <div>Physical and Chemical Change</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5D.3 Collect, organize, and interpret data using various tools (e.g., litmus
                paper, pH paper, cabbage juice) regarding neutralization of acids and bases using
                common substances
              </div>
            </div>
            <div className="row">
              <div>Physical and Chemical Change</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5D.4 Build a model to explain that chemical reactions can store (formation of
                bonds) or release energy (breaking of bonds).
              </div>
            </div>
            <div className="row">
              <div>Physical and Chemical Change</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5E.1 Conduct simple scientific investigations to show that total mass is not
                altered during a chemical reaction in a closed system. Compare results of
                investigations to AntoineLaurent Lavoisier's discovery of the law of conservation of
                mass.
              </div>
            </div>
            <div className="row">
              <div>Physical and Chemical Change</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5E.2 Analyze data from investigations to explain why the total mass of the
                product in an open system appears to be less than the mass of reactants.
              </div>
            </div>
            <div className="row">
              <div>Physical and Chemical Change</div>
              <div>7</div>
              <div>Physical Science</div>
              <div>P.7.5 Organization of Matter and Chemical Interactions</div>
              <div>
                P.7.5E.3 Compare and contrast balanced and unbalanced chemical equations to
                demonstrate the number of atoms does not change in the reaction.
              </div>
            </div>
            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth and Space Science</div>
              <div>E.7.9 Earth's Systems and Cycles</div>
              <div>
                E.7.9A.1 Analyze and interpret weather patterns from various regions to
                differentiate between weather and climate.
              </div>
            </div>
            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth and Space Science</div>
              <div>E.7.9 Earth's Systems and Cycles</div>
              <div>
                E.7.9A.2 Analyze evidence to explain the weather conditions that result from the
                relationship between the movement of water and air masses.
              </div>
            </div>
            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth and Space Science</div>
              <div>E.7.9 Earth's Systems and Cycles</div>
              <div>
                E.7.9A.3 Interpret atmospheric data from satellites, radar, and weather maps to
                predict weather patterns and conditions.
              </div>
            </div>
            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth and Space Science</div>
              <div>E.7.9 Earth's Systems and Cycles</div>
              <div>
                E.7.9A.4 Construct an explanation for how climate is determined in an area using
                global and surface features (e.g. latitude, elevation, shape of the land, distance
                from water, global winds and ocean currents).
              </div>
            </div>
            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth and Space Science</div>
              <div>E.7.9 Earth's Systems and Cycles</div>
              <div>
                E.7.9A.5 Analyze models to explain the cause and effect relationship between solar
                energy and convection and the resulting weather patterns and climate conditions
              </div>
            </div>
            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth and Space Science</div>
              <div>E.7.9 Earth's Systems and Cycles</div>
              <div>
                E.7.9A.6 Research and use models to explain what type of weather (thunderstorms,
                hurricanes, and tornadoes) results from the movement and interactions of air masses,
                high and low pressure systems, and frontal boundaries.
              </div>
            </div>
            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth and Space Science</div>
              <div>E.7.9 Earth's Systems and Cycles</div>
              <div>
                E.7.9A.7 Interpret topographic maps to predict how local and regional geography
                affect weather patterns and make them difficult to predict.
              </div>
            </div>
            <div className="row">
              <div>Climate Change</div>
              <div>7</div>
              <div>Earth and Space Science</div>
              <div>E.7.9 Earth's Systems and Cycles</div>
              <div>
                E.7.9B.1 Read and evaluate scientific or technical information assessing the
                evidence and bias of each source to explain the causes and effects of climate
                change.
              </div>
            </div>
            <div className="row">
              <div>Climate Change</div>
              <div>7</div>
              <div>Earth and Space Science</div>
              <div>E.7.9 Earth's Systems and Cycles</div>
              <div>
                E.7.9B.2 Interpret data about the relationship between the release of carbon dioxide
                from burning fossil fuels into the atmosphere and the presence of greenhouse gases.
              </div>
            </div>
            <div className="row">
              <div>Climate Change</div>
              <div>7</div>
              <div>Earth and Space Science</div>
              <div>E.7.9 Earth's Systems and Cycles</div>
              <div>
                E.7.9B.3 Engage in scientific argument based on current evidence to determine
                whether climate change happens naturally or is being accelerated through the
                influence of man
              </div>
            </div>
            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth and Space Science</div>
              <div>E.7.9 Earth's Systems and Cycles</div>
              <div>
                E.7.9C.1 Construct models and diagrams to illustrate how the tilt of Earth's axis
                results in differences in intensity of sunlight on the Earth's hemispheres
                throughout the course of one full revolution around the Sun.
              </div>
            </div>
            <div className="row">
              <div>Earth Systems</div>
              <div>7</div>
              <div>Earth and Space Science</div>
              <div>E.7.9 Earth's Systems and Cycles</div>
              <div>
                E.7.9C.2 Investigate how variations of sunlight intensity experienced by each
                hemisphere (to include the equator and poles) create the four seasons.
              </div>
            </div>
            <div className="row">
              <div>Genetics</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.2 Reproduction and Heredity</div>
              <div>
                L.8.2A.1 Obtain and communicate information about the relationship of genes,
                chromosomes, and DNA, and construct explanations comparing their relationship to
                inherited characteristics.
              </div>
            </div>
            <div className="row">
              <div>Genetics</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.2 Reproduction and Heredity</div>
              <div>
                L.8.2A.2 Create a diagram of mitosis and explain its role in asexual reproduction,
                which results in offspring with identical genetic information.
              </div>
            </div>
            <div className="row">
              <div>Genetics</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.2 Reproduction and Heredity</div>
              <div>
                L.8.2A.3 Construct explanations of how genetic information is transferred during
                meiosis
              </div>
            </div>
            <div className="row">
              <div>Genetics</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.2 Reproduction and Heredity</div>
              <div>
                L.8.2A.4 Engage in discussion using models and evidence to explain that sexual
                reproduction produces offspring that have a new combination of genetic information
                different from either parent
              </div>
            </div>
            <div className="row">
              <div>Plants</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.2 Reproduction and Heredity</div>
              <div>
                L.8.2A.5 Compare and contrast advantages and disadvantages of asexual and sexual
                reproduction.
              </div>
            </div>
            <div className="row">
              <div>Plants</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.2 Reproduction and Heredity</div>
              <div>
                L.8.2B.1 Construct an argument based on evidence for how environmental and genetic
                factors influence the growth of organisms
              </div>
            </div>
            <div className="row">
              <div>Genetics</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.2 Reproduction and Heredity</div>
              <div>
                L.8.2B.2 Use various scientific resources to research and support the historical
                findings of Gregor Mendel to explain the basic principles of heredity.
              </div>
            </div>
            <div className="row">
              <div>Genetics</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.2 Reproduction and Heredity</div>
              <div>
                L.8.2B.3 Use mathematical and computational thinking to analyze data and make
                predictions about the outcome of specific genetic crosses (monohybrid Punnett
                Squares) involving simple dominant/recessive traits.
              </div>
            </div>
            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.2 Reproduction and Heredity</div>
              <div>
                L.8.2B.4 Debate the ethics of artificial selection (selective breeding, genetic
                engineering) and the societal impacts of humans changing the inheritance of desired
                traits in organisms.
              </div>
            </div>
            <div className="row">
              <div>Genetics</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.2 Reproduction and Heredity</div>
              <div>
                L.8.2C.1 Communicate through diagrams that chromosomes contain many distinct genes
                and that each gene holds the instructions for the production of specific proteins,
                which in turn affects the traits of the individual (not to include transcription or
                translation).
              </div>
            </div>
            <div className="row">
              <div>Genetics</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.2 Reproduction and Heredity</div>
              <div>
                L.8.2C.2 Construct scientific arguments from evidence to support claims about the
                potentially harmful, beneficial, or neutral effects of genetic mutations on
                organisms.
              </div>
            </div>
            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.4 Adaptation and Diversity</div>
              <div>
                L.8.4A.1 Use various scientific resources to analyze the historical findings of
                Charles Darwin to explain basic principles of natural selection.
              </div>
            </div>
            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.4 Adaptation and Diversity</div>
              <div>
                L.8.4A.2 Investigate to construct explanations about natural selection that connect
                growth, survival, and reproduction to genetic factors, environmental factors, food
                intake, and interactions with other organisms.
              </div>
            </div>
            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.4 Adaptation and Diversity</div>
              <div>
                L.8.4B.1 Analyze and interpret data (e.g. pictures, graphs) to explain how natural
                selection may lead to increases and decreases of specific traits in populations over
                time.
              </div>
            </div>
            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.4 Adaptation and Diversity</div>
              <div>
                L.8.4B.2 Construct written and verbal explanations to describe how genetic
                variations of traits in a population increase some organisms' probability of
                surviving and reproducing in a specific environment.
              </div>
            </div>
            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.4 Adaptation and Diversity</div>
              <div>
                L.8.4B.3 Obtain and evaluate scientific information to explain that separated
                populations, that remain separated, can evolve through mutations to become a new
                species (speciation).
              </div>
            </div>
            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Life Science</div>
              <div>L.8.4 Adaptation and Diversity</div>
              <div>
                L.8.4B.4 Analyze displays of pictorial data to compare and contrast embryological
                and homologous/analogous structures across multiple species to identify evolutionary
                relationships.
              </div>
            </div>
            <div className="row">
              <div>Waves</div>
              <div>8</div>
              <div>Physical Science</div>
              <div>P.8.6 Motions, Forces, and Energy</div>
              <div>
                P.8.6.1 Collect, organize, and interpret data about the characteristics of sound and
                light waves to construct explanations about the relationship between matter and
                energy.
              </div>
            </div>
            <div className="row">
              <div>Waves</div>
              <div>8</div>
              <div>Physical Science</div>
              <div>P.8.6 Motions, Forces, and Energy</div>
              <div>
                P.8.6.2 Investigate research-based mechanisms for capturing and converting wave
                energy (frequency, amplitude, wavelength, and speed) into electrical energy.
              </div>
            </div>
            <div className="row">
              <div>Waves</div>
              <div>8</div>
              <div>Physical Science</div>
              <div>P.8.6 Motions, Forces, and Energy</div>
              <div>
                P.8.6.3 Conduct simple investigations about the performance of waves to describe
                their behavior (e.g., refraction, reflection, transmission, and absorption) as they
                interact with various materials (e.g., lenses, mirrors, and prisms).
              </div>
            </div>
            <div className="row">
              <div>Waves</div>
              <div>8</div>
              <div>Physical Science</div>
              <div>P.8.6 Motions, Forces, and Energy</div>
              <div>
                P.8.6.4 Use scientific processes to plan and conduct controlled investigations to
                conclude sound is a wave phenomenon that is characterized by amplitude and
                frequency.
              </div>
            </div>
            <div className="row">
              <div>Waves</div>
              <div>8</div>
              <div>Physical Science</div>
              <div>P.8.6 Motions, Forces, and Energy</div>
              <div>
                P.8.6.5 Conduct scientific investigations that describe the behavior of sound when
                resonance changes (e.g., waves in a stretched string and design of musical
                instruments).
              </div>
            </div>
            <div className="row">
              <div>Waves</div>
              <div>8</div>
              <div>Physical Science</div>
              <div>P.8.6 Motions, Forces, and Energy</div>
              <div>
                P.8.6.6 Obtain and evaluate scientific information to explain the relationship
                between seeing color and the transmission, absorption, or reflection of light waves
                by various materials.
              </div>
            </div>
            <div className="row">
              <div>Waves</div>
              <div>8</div>
              <div>Physical Science</div>
              <div>P.8.6 Motions, Forces, and Energy</div>
              <div>
                P.8.6.7 Research the historical significance of wave technology to explain how
                digitized tools have evolved to encode and transmit information (e.g., telegraph,
                cell phones, and wireless computer networks).
              </div>
            </div>
            <div className="row">
              <div>Waves</div>
              <div>8</div>
              <div>Physical Science</div>
              <div>P.8.6 Motions, Forces, and Energy</div>
              <div>
                P.8.6.8 Compare and contrast the behavior of sound and light waves to determine
                which types of waves need a medium for transmission.
              </div>
            </div>
            <div className="row">
              <div>Human Impacts on Ecosystems</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.7 Earth's Structure and History</div>
              <div>
                E.8.7.1 Use scientific evidence to create a timeline of Earth's history that depicts
                relative dates from index fossil records and layers of rock (strata).
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.7 Earth's Structure and History</div>
              <div>
                E.8.7.2 Create a model of the processes involved in the rock cycle and relate it to
                the fossil record.
              </div>
            </div>
            <div className="row">
              <div>Human Impacts on Ecosystems</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.7 Earth's Structure and History</div>
              <div>
                E.8.7.3 Construct and analyze scientific arguments to support claims that most
                fossil evidence is an indication of the diversity of life that was present on Earth
                and that relationships exist between past and current life forms.
              </div>
            </div>
            <div className="row">
              <div>Human Impacts on Ecosystems</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.7 Earth's Structure and History</div>
              <div>
                E.8.7.4 Use research and evidence to document how evolution has been shaped both
                gradually and through mass extinction by Earth's varying geological conditions
                (e.g., climate change, meteor impacts, and volcanic eruptions).
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.9 Earth's Systems and Cycles</div>
              <div>
                E.8.9A.1 Investigate and explain how the flow of Earth's internal energy drives the
                cycling of matter through convection currents between Earth's surface and the deep
                interior causing plate movements.
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.9 Earth's Systems and Cycles</div>
              <div>
                E.8.9A.2 Explore and debate theories of plate tectonics to form conclusions about
                past and current movements of rocks at Earth's surface throughout history.
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.9 Earth's Systems and Cycles</div>
              <div>
                E.8.9A.3 Map land and water patterns from various time periods and use rocks and
                fossils to report evidence of how Earth's plates have moved great distances,
                collided, and spread apart.
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.9 Earth's Systems and Cycles</div>
              <div>
                E.8.9A.4 Research and assess the credibility of scientific ideas to debate and
                discuss how Earth's constructive and destructive processes have changed Earth's
                surface at varying time and spatial scales.
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.9 Earth's Systems and Cycles</div>
              <div>
                E.8.9A.5 Use models that demonstrate convergent and divergent plate movements that
                are responsible for most landforms and the distribution of most rocks and minerals
                within Earth's crust.
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.9 Earth's Systems and Cycles</div>
              <div>
                E.8.9A.6 Design and conduct investigations to evaluate the chemical and physical
                processes involved in the formation of soils.
              </div>
            </div>
            <div className="row">
              <div>Hydrosphere</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.9 Earth's Systems and Cycles</div>
              <div>
                E.8.9A.7 Explain the interconnected relationship between surface water and
                groundwater.
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.9 Earth's Systems and Cycles</div>
              <div>
                E.8.9B.1 Research and map various types of natural hazards to determine their impact
                on society.
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.9 Earth's Systems and Cycles</div>
              <div>
                E.8.9B.2 Compare and contrast technologies that predict natural hazards to identify
                which types of technologies are most effective.
              </div>
            </div>
            <div className="row">
              <div>Active Earth</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.9 Earth's Systems and Cycles</div>
              <div>
                E.8.9B.3 Using an engineering design process, create mechanisms to improve community
                resilience, which safeguard against natural hazards (e.g., building restrictions in
                flood or tidal zones, regional watershed management, Firewise construction).*
              </div>
            </div>
            <div className="row">
              <div>Energy Conservation</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.10 Earth's Resources</div>
              <div>
                E.8.10.1 Read and evaluate scientific information about advancements in renewable
                and nonrenewable resources. Propose and defend ways to decrease national and global
                dependency on nonrenewable resources.
              </div>
            </div>
            <div className="row">
              <div>Hydrosphere</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.10 Earth's Resources</div>
              <div>
                E.8.10.2 Create and defend a proposal for reducing the environmental effects humans
                have on Earth (e.g., population increases, consumer demands, chemical pollution,
                deforestation, and change in average annual temperature).
              </div>
            </div>
            <div className="row">
              <div>Energy Conservation</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.10 Earth's Resources</div>
              <div>
                E.8.10.3 Using scientific data, debate the societal advantages and disadvantages of
                technological advancements in renewable energy sources.
              </div>
            </div>
            <div className="row">
              <div>Energy Conservation</div>
              <div>8</div>
              <div>Earth and Space Science</div>
              <div>E.8.10 Earth's Resources</div>
              <div>
                E.8.10.4 Using an engineering design process, develop a system to capture and
                distribute thermal energy that makes renewable energy more readily available and
                reduces human impact on the environment (e.g., building solar water heaters,
                conserving home energy).*
              </div>
            </div>
          </div>
        </div>
      </AlignmentSection>
    </MississippiAlignmentLayout>
  );
}

export default TwentyTwentyFourMississippiAlignmentPage;
